import {
  Building2,
  Mail,
  Phone,
  MoreVertical,
  Eye,
  Edit,
  Trash2,
  CheckCircle2,
  AlertTriangle,
  Clock,
  Link2,
  Send,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { cn } from "@/lib/utils";
import type { Subcontractor } from "@/pages/Subcontractors";

interface SubcontractorCardProps {
  subcontractor: Subcontractor;
  onView: () => void;
  onEdit: () => void;
  onDelete: () => void;
  onInvite: () => void;
}

const SubcontractorCard = ({
  subcontractor,
  onView,
  onEdit,
  onDelete,
  onInvite,
}: SubcontractorCardProps) => {
  const { compliance_score, status } = subcontractor;

  const getComplianceInfo = () => {
    if (compliance_score >= 80) {
      return {
        label: "Conforme",
        color: "text-emerald-600",
        bgColor: "bg-emerald-500",
        icon: CheckCircle2,
      };
    }
    if (compliance_score >= 50) {
      return {
        label: "Partiel",
        color: "text-amber-600",
        bgColor: "bg-amber-500",
        icon: Clock,
      };
    }
    return {
      label: "Non conforme",
      color: "text-destructive",
      bgColor: "bg-destructive",
      icon: AlertTriangle,
    };
  };

  const getStatusBadge = () => {
    switch (status) {
      case "active":
        return <Badge variant="outline" className="bg-emerald-500/10 text-emerald-600 border-emerald-500/20">Actif</Badge>;
      case "inactive":
        return <Badge variant="outline" className="bg-muted text-muted-foreground">Inactif</Badge>;
      case "blocked":
        return <Badge variant="outline" className="bg-destructive/10 text-destructive border-destructive/20">Bloqué</Badge>;
      case "pending":
        return <Badge variant="outline" className="bg-amber-500/10 text-amber-600 border-amber-500/20">En attente</Badge>;
      default:
        return null;
    }
  };

  const complianceInfo = getComplianceInfo();
  const ComplianceIcon = complianceInfo.icon;

  return (
    <Card className="hover:shadow-md transition-shadow cursor-pointer" onClick={onView}>
      <CardContent className="p-4">
        <div className="flex items-start gap-4">
          {/* Avatar */}
          <div className="p-3 rounded-xl bg-primary/10 shrink-0">
            <Building2 className="w-6 h-6 text-primary" />
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2">
              <div>
                <h3 className="font-semibold truncate">{subcontractor.company_name}</h3>
                {subcontractor.siret && (
                  <p className="text-xs text-muted-foreground">
                    SIRET : {subcontractor.siret}
                  </p>
                )}
              </div>

              {/* Actions */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                  <Button variant="ghost" size="icon" className="shrink-0 h-8 w-8">
                    <MoreVertical className="w-4 h-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onView(); }}>
                    <Eye className="w-4 h-4 mr-2" />
                    Voir les détails
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onInvite(); }}>
                    <Link2 className="w-4 h-4 mr-2" />
                    Inviter à déposer
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onEdit(); }}>
                    <Edit className="w-4 h-4 mr-2" />
                    Modifier
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <DropdownMenuItem
                        className="text-destructive"
                        onSelect={(e) => e.preventDefault()}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Supprimer
                      </DropdownMenuItem>
                    </AlertDialogTrigger>
                    <AlertDialogContent onClick={(e) => e.stopPropagation()}>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Supprimer ce prestataire ?</AlertDialogTitle>
                        <AlertDialogDescription>
                          Cette action supprimera définitivement {subcontractor.company_name} et tous ses documents associés.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Annuler</AlertDialogCancel>
                        <AlertDialogAction onClick={onDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                          Supprimer
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>

            {/* Contact info */}
            <div className="flex flex-wrap gap-3 mt-2 text-sm text-muted-foreground">
              {subcontractor.contact_email && (
                <span className="flex items-center gap-1">
                  <Mail className="w-3 h-3" />
                  <span className="truncate max-w-[150px]">{subcontractor.contact_email}</span>
                </span>
              )}
              {subcontractor.contact_phone && (
                <span className="flex items-center gap-1">
                  <Phone className="w-3 h-3" />
                  {subcontractor.contact_phone}
                </span>
              )}
            </div>

            {/* Compliance Score */}
            <div className="mt-4">
              <div className="flex items-center justify-between mb-1">
                <div className="flex items-center gap-2">
                  <ComplianceIcon className={cn("w-4 h-4", complianceInfo.color)} />
                  <span className={cn("text-sm font-medium", complianceInfo.color)}>
                    {complianceInfo.label}
                  </span>
                </div>
                <span className={cn("text-sm font-bold", complianceInfo.color)}>
                  {compliance_score}%
                </span>
              </div>
              <Progress 
                value={compliance_score} 
                className="h-2"
              />
            </div>

            {/* Status Badge + Invite Button */}
            <div className="mt-3 flex items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                {getStatusBadge()}
                {subcontractor.invitation_sent_at && (
                  <Badge variant="outline" className="text-xs">
                    <Send className="w-3 h-3 mr-1" />
                    Invité
                  </Badge>
                )}
              </div>
              <Button
                variant="ghost"
                size="sm"
                className="h-7 text-xs"
                onClick={(e) => { e.stopPropagation(); onInvite(); }}
              >
                <Link2 className="w-3 h-3 mr-1" />
                Inviter
              </Button>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default SubcontractorCard;
